import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { UUID } from "@/types/databaseRows";

/**
 * ActiveRowState manages temporary UI state for tracking active/selected rows
 * across different tables or lists in the application.
 *
 * This slice supports:
 * - Single active row per table (e.g., for detail panels)
 * - Multiple selected rows per table (e.g., for bulk operations)
 *
 * Keys can represent any table, list, or component that needs row tracking.
 * Row IDs are stored as strings to allow flexibility across different ID formats.
 */
export interface ActiveRowState {
  activeRows: Record<string, string | null>;
  selectedRows: Record<string, string[]>;
}

const initialState: ActiveRowState = {
  activeRows: {},
  selectedRows: {},
};

const activeRowSlice = createSlice({
  name: "activeRow",
  initialState,
  reducers: {
    setActiveRow: (
      state,
      action: PayloadAction<{ tableName: string; rowId: string | null }>
    ) => {
      state.activeRows[action.payload.tableName] = action.payload.rowId;
    },
    clearActiveRow: (state, action: PayloadAction<string>) => {
      delete state.activeRows[action.payload];
    },
    clearAllActiveRows: (state) => {
      state.activeRows = {};
    },
    // Multi-selection actions
    toggleSelectedRow: (
      state,
      action: PayloadAction<{ tableName: string; rowId: string }>
    ) => {
      const { tableName, rowId } = action.payload;
      if (!state.selectedRows[tableName]) {
        state.selectedRows[tableName] = [];
      }
      const index = state.selectedRows[tableName].indexOf(rowId);
      if (index === -1) {
        state.selectedRows[tableName].push(rowId);
      } else {
        state.selectedRows[tableName].splice(index, 1);
      }
    },
    setSelectedRows: (
      state,
      action: PayloadAction<{ tableName: string; rowIds: string[] }>
    ) => {
      state.selectedRows[action.payload.tableName] = action.payload.rowIds;
    },
    clearSelectedRows: (state, action: PayloadAction<string>) => {
      state.selectedRows[action.payload] = [];
    },
    clearAllSelectedRows: (state) => {
      state.selectedRows = {};
    },
  },
});

export const { 
  setActiveRow, 
  clearActiveRow, 
  clearAllActiveRows,
  toggleSelectedRow,
  setSelectedRows,
  clearSelectedRows,
  clearAllSelectedRows,
} = activeRowSlice.actions;

export const selectActiveRow = (
  state: { activeRow: ActiveRowState },
  tableName: string
): UUID | null => {
  // Row IDs stored in activeRows are always UUIDs from database rows
  return (state.activeRow.activeRows[tableName] ?? null) as UUID | null;
};

export const selectAllActiveRows = (state: {
  activeRow: ActiveRowState;
}): Record<string, string | null> => {
  return state.activeRow.activeRows;
};

export const selectSelectedRows = (
  state: { activeRow: ActiveRowState },
  tableName: string
): string[] => {
  return state.activeRow.selectedRows[tableName] ?? [];
};

export const selectAllSelectedRows = (state: {
  activeRow: ActiveRowState;
}): Record<string, string[]> => {
  return state.activeRow.selectedRows;
};

export const selectIsRowSelected = (
  state: { activeRow: ActiveRowState },
  tableName: string,
  rowId: string
): boolean => {
  const selected = state.activeRow.selectedRows[tableName] ?? [];
  return selected.includes(rowId);
};

export default activeRowSlice.reducer;

// Hook for managing active row state for a specific table
// Generic type parameter allows specifying the ID type (e.g., UUID) for type safety
export function useActiveRow<IdType extends string = string>(tableName: string) {
  const dispatch = useDispatch();
  const activeRowId = useSelector(
    (state: { activeRow: ActiveRowState }) => selectActiveRow(state, tableName)
  ) as IdType | null;

  const setActive = useCallback((rowId: IdType | null) => {
    dispatch(setActiveRow({ tableName, rowId }));
  }, [dispatch, tableName]);

  const clear = useCallback(() => {
    dispatch(clearActiveRow(tableName));
  }, [dispatch, tableName]);

  return {
    activeRowId,
    setActive,
    clear,
  };
}

// Hook for accessing all active rows
export function useAllActiveRows() {
  const dispatch = useDispatch();
  const activeRows = useSelector(
    (state: { activeRow: ActiveRowState }) => selectAllActiveRows(state)
  );

  const clearAll = useCallback(() => {
    dispatch(clearAllActiveRows());
  }, [dispatch]);

  return {
    activeRows,
    clearAll,
  };
}

// Hook for managing multi-selection for a specific table
export function useSelectedRows(tableName: string) {
  const dispatch = useDispatch();
  const selectedRowIds = useSelector(
    (state: { activeRow: ActiveRowState }) => selectSelectedRows(state, tableName)
  );

  const toggleSelection = useCallback((rowId: string) => {
    dispatch(toggleSelectedRow({ tableName, rowId }));
  }, [dispatch, tableName]);

  const setSelection = useCallback((rowIds: string[]) => {
    dispatch(setSelectedRows({ tableName, rowIds }));
  }, [dispatch, tableName]);

  const clearSelection = useCallback(() => {
    dispatch(clearSelectedRows(tableName));
  }, [dispatch, tableName]);

  const isSelected = useCallback((rowId: string) => {
    return selectedRowIds.includes(rowId);
  }, [selectedRowIds]);

  return {
    selectedRowIds,
    toggleSelection,
    setSelection,
    clearSelection,
    isSelected,
  };
}

// Hook for accessing all selected rows
export function useAllSelectedRows() {
  const dispatch = useDispatch();
  const selectedRows = useSelector(
    (state: { activeRow: ActiveRowState }) => selectAllSelectedRows(state)
  );

  const clearAll = useCallback(() => {
    dispatch(clearAllSelectedRows());
  }, [dispatch]);

  return {
    selectedRows,
    clearAll,
  };
}
